import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Shield, Send, MessageSquare, CheckCircle2, ExternalLink } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }
};
export function FeedbackModal({
  isOpen,
  onClose
}: FeedbackModalProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [isGdprChecked, setIsGdprChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    showToast
  } = useToast();
  // Google Business Profile Link (Placeholder)
  const GOOGLE_REVIEW_LINK = 'https://g.page/r/Cat9FuHdU1RAEBM/review';
  const handleRate = (star: number) => {
    setRating(star);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isGdprChecked) {
      showToast('Please accept the GDPR compliance checkbox', 'error');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    if (rating <= 3) {
      showToast('Feedback sent to our forensic team', 'success');
    }
  };
  const handleGoogleRedirect = () => {
    window.open(GOOGLE_REVIEW_LINK, '_blank');
    onClose();
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-2348">
      {isOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-2349">
          <motion.div className="bg-[#050505] border border-gray-800 max-w-lg w-full p-8 relative shadow-2xl overflow-hidden" variants={modalVariants} initial="hidden" animate="visible" exit="exit" data-id="element-2350">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#DC2626]" data-id="element-2351" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#DC2626]" data-id="element-2352" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#DC2626]" data-id="element-2353" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#DC2626]" data-id="element-2354" />

            {/* Scanning Line Animation */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#DC2626] to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-2355" />
            <style data-id="element-2356">{`
              @keyframes scan {
                0%, 100% { transform: translateX(-100%); }
                50% { transform: translateX(100%); }
              }
            `}</style>

            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors p-2" data-id="element-2357">
              <X size={20} data-id="element-2358" />
            </button>

            {/* Content */}
            <div className="text-center mb-8" data-id="element-2359">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-full mb-4" data-id="element-2360">
                <MessageSquare className="text-[#DC2626]" size={20} data-id="element-2361" />
              </div>
              <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2" data-id="element-2362">
                Share Your Story
              </h2>
              <p className="text-gray-400 font-mono text-xs" data-id="element-2363">
                Your experience helps validate our forensic engine.
              </p>
            </div>

            {!isSubmitted ? <div className="space-y-8" data-id="element-2364">
                {/* Star Rating */}
                <div className="flex justify-center gap-2" data-id="element-2365">
                  {[1, 2, 3, 4, 5].map(star => <motion.button key={star} whileHover={{
              scale: 1.1
            }} whileTap={{
              scale: 0.9
            }} onClick={() => handleRate(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="focus:outline-none transition-colors" data-id="element-2366">
                      <Star size={32} className={`${star <= (hoverRating || rating) ? 'fill-[#FFD700] text-[#FFD700]' : 'text-gray-700'} transition-colors duration-200`} strokeWidth={1.5} data-id="element-2367" />
                    </motion.button>)}
                </div>

                <AnimatePresence mode="wait" data-id="element-2368">
                  {/* High Rating Flow (4-5 Stars) */}
                  {rating >= 4 && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="text-center space-y-6" data-id="element-2369">
                      <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 p-6 rounded-lg" data-id="element-2370">
                        <p className="text-[#FFD700] font-bold text-lg mb-2" data-id="element-2371">
                          Your clarity helps others.
                        </p>
                        <p className="text-gray-400 text-sm mb-6" data-id="element-2372">
                          Would you mind sharing this on Google? It helps
                          victims find us faster.
                        </p>

                        <button onClick={handleGoogleRedirect} className="w-full bg-[#FFD700] text-black font-black py-3 px-6 rounded hover:bg-[#FFD700]/90 transition-all flex items-center justify-center gap-2 uppercase tracking-wider text-sm" data-id="element-2373">
                          <ExternalLink size={16} data-id="element-2374" />
                          Review on Google
                        </button>
                      </div>

                      <div className="flex items-center justify-center gap-2 text-gray-500 text-xs font-mono" data-id="element-2375">
                        <Shield size={12} data-id="element-2376" />
                        <span data-id="element-2377">GDPR Compliant & Anonymous</span>
                      </div>
                    </motion.div>}

                  {/* Low Rating Flow (1-3 Stars) */}
                  {rating > 0 && rating <= 3 && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} data-id="element-2378">
                      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2379">
                        <div data-id="element-2380">
                          <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-2381">
                            Private Support Channel
                          </label>
                          <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} placeholder="How can we improve our forensic accuracy?" rows={4} className="w-full bg-gray-900/50 border border-gray-800 text-white p-4 focus:border-[#DC2626] focus:outline-none transition-colors text-sm font-mono resize-none" required data-id="element-2382" />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-2383">
                          <div className="relative flex items-center" data-id="element-2384">
                            <input type="checkbox" checked={isGdprChecked} onChange={e => setIsGdprChecked(e.target.checked)} className="peer sr-only" data-id="element-2385" />
                            <div className="w-5 h-5 border border-gray-600 peer-checked:bg-[#DC2626] peer-checked:border-[#DC2626] transition-all" data-id="element-2386" />
                            <CheckCircle2 size={12} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" data-id="element-2387" />
                          </div>
                          <span className="text-xs text-gray-500 group-hover:text-gray-400 transition-colors" data-id="element-2388">
                            I agree to process this feedback anonymously in
                            compliance with GDPR.
                          </span>
                        </label>

                        <button type="submit" disabled={isSubmitting || !feedbackText} className="w-full bg-[#DC2626] text-white font-bold py-3 px-6 hover:bg-red-700 transition-all flex items-center justify-center gap-2 uppercase tracking-wider text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2389">
                          {isSubmitting ? <span className="animate-pulse" data-id="element-2390">
                              Transmitting...
                            </span> : <>
                              <Send size={16} data-id="element-2391" />
                              Send to Admin
                            </>}
                        </button>
                      </form>
                    </motion.div>}
                </AnimatePresence>
              </div> : <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-8" data-id="element-2392">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full mb-6" data-id="element-2393">
                  <CheckCircle2 className="text-green-500" size={32} data-id="element-2394" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-2395">
                  Feedback Received
                </h3>
                <p className="text-gray-400 text-sm font-mono mb-8" data-id="element-2396">
                  Our team will review your report to improve our detection
                  algorithms.
                </p>
                <button onClick={onClose} className="bg-gray-800 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gray-700 transition-colors" data-id="element-2397">
                  Close
                </button>
              </motion.div>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}